import * as Sentry from "@sentry/react";

import { preferenciaLigada } from "./preferencias";

/**
 * Relatório de erros.
 *
 * Um jogo de gestão vive de temporadas longas. Um defeito que só aparece na
 * rodada quarenta, num save específico, não chega por e-mail de jogador: chega
 * por relatório automático ou não chega.
 *
 * O que sobe é o mínimo para reproduzir: a semente do mundo, a rodada e a
 * versão do motor. **O save nunca sobe.** Ele tem alguns megabytes, carrega
 * nomes que o jogador escreveu e não cabe num relatório de erro.
 *
 * Sem endereço configurado, nada é enviado e o jogo funciona igual. É assim que
 * roda em desenvolvimento e na máquina de quem clonou o projeto.
 *
 * **Nada é montado enquanto a pessoa não disser que sim** (a tela de opções é
 * onde ela diz). Não basta deixar de enviar os erros: o próprio `Sentry.init`
 * abre uma sessão de saúde da versão, e essa sessão não passa por filtro
 * nenhum. Só não chamar o `init` cumpre o que `docs/LEGAL.md` 1 promete, que é
 * o modo solo não mandar nada para servidor nenhum.
 *
 * Desligar no meio do caminho é o outro lado, e aí o cliente já está de pé.
 * Por isso os dois filtros abaixo releem a escolha a cada evento: quem desmarca
 * a caixa para de mandar naquele instante, sem precisar reabrir o jogo.
 */

/** Onde o jogo está rodando, para separar os relatórios por plataforma. */
function plataforma(): string {
  if (typeof window === "undefined") return "desconhecida";
  // O Tauri injeta este objeto na janela. Não vale importar o pacote dele aqui,
  // porque na web ele não existe e o import quebraria a build.
  if ("__TAURI_INTERNALS__" in window) return "desktop";
  const agente = navigator.userAgent ?? "";
  if (/Android/i.test(agente)) return "android";
  if (/iPhone|iPad|iPod/i.test(agente)) return "ios";
  return "web";
}

/** Esta build tem para onde mandar relatório. Sem isso, não há o que oferecer. */
export function relatorioDeErrosConfigurado(): boolean {
  return Boolean(import.meta.env.VITE_SENTRY_DSN);
}

/** A pessoa aceitou mandar relatório deste aparelho. */
export function relatorioDeErrosLigado(): boolean {
  return preferenciaLigada("relatorioDeErros");
}

/**
 * Chamado na abertura do jogo e de novo quando alguém marca a caixa.
 *
 * A segunda chamada precisa existir porque ligar na tela de opções tem que
 * valer para o erro de agora, não para o da próxima vez que o jogo abrir. E
 * precisa ser barata: com o cliente já de pé, ela não faz nada.
 */
export function iniciarRelatorioDeErros(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;
  if (!relatorioDeErrosLigado()) return;
  if (Sentry.getClient()) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: `legacy-manager-client@${import.meta.env.VITE_APP_VERSION ?? "0.0.0"}`,
    dist: plataforma(),
    tracesSampleRate: 0.05,
    // Sem gravação de sessão: o jogo mostra nomes e decisões do jogador.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    sendDefaultPii: false,
    ignoreErrors: ["ResizeObserver loop", "Network request failed"],
    // Erro e medida de desempenho saem por portas diferentes do SDK, e a
    // escolha vale para as duas: filtrar só uma deixaria a outra subindo.
    beforeSend: (evento) => (relatorioDeErrosLigado() ? evento : null),
    beforeSendTransaction: (evento) => (relatorioDeErrosLigado() ? evento : null),
  });

  Sentry.setTag("plataforma", plataforma());
}

/**
 * Contexto do jogo em andamento.
 *
 * Chamado a cada rodada. Semente e rodada bastam para reproduzir qualquer
 * defeito do motor, porque a simulação é determinística.
 */
export function contextoDaPartida(info: {
  seed: string;
  season: number;
  round: number;
  engineVersion: string;
  schemaVersion: number;
  modo: "solo" | "hotseat" | "online";
}): void {
  if (!relatorioDeErrosLigado()) return;
  Sentry.setTags({
    modo: info.modo,
    engineVersion: info.engineVersion,
    saveSchemaVersion: String(info.schemaVersion),
  });
  Sentry.setContext("partida", {
    semente: info.seed,
    temporada: info.season,
    rodada: info.round,
  });
}

/** Erro que o jogo tratou mas quer registrar, como falha ao salvar. */
export function registrarAviso(mensagem: string, detalhes?: Record<string, unknown>): void {
  if (!relatorioDeErrosLigado()) return;
  Sentry.captureMessage(mensagem, { level: "warning", extra: detalhes });
}

// ---------------------------------------------------------------------------
// Relato ao próprio servidor
// ---------------------------------------------------------------------------

/**
 * Além do GlitchTip, o erro vai para `POST /errors` da nossa API, que é o que
 * o painel da equipe lista. Sem sessão também: a tela que quebrou pode ser a
 * de entrar. Mesma escolha do jogador que vale para o GlitchTip, e no máximo
 * cinco relatos por abertura do jogo, sem repetir a mesma mensagem.
 */
const RELATOS_POR_SESSAO = 5;
let relatosFeitos = 0;
const mensagensRelatadas = new Set<string>();

function enderecoDaApi(): string {
  const configurado = import.meta.env.VITE_API_URL?.replace(/\/+$/, "");
  if (configurado) return configurado;
  return typeof window === "undefined" ? "http://localhost:3000" : window.location.origin;
}

function plataformaDoRelato(): "web" | "windows" | "android" | "ios" | "unknown" {
  const onde = plataforma();
  if (onde === "desktop") return "windows";
  if (onde === "android" || onde === "ios" || onde === "web") return onde;
  return "unknown";
}

export async function relatarAoServidor(causa: unknown, contexto?: Record<string, unknown>): Promise<void> {
  if (!relatorioDeErrosLigado()) return;
  if (relatosFeitos >= RELATOS_POR_SESSAO) return;

  const erro = causa instanceof Error ? causa : new Error(typeof causa === "string" ? causa : JSON.stringify(causa ?? "erro sem mensagem"));
  const mensagem = erro.message.slice(0, 1_000);
  if (!mensagem || mensagensRelatadas.has(mensagem)) return;
  mensagensRelatadas.add(mensagem);
  relatosFeitos += 1;

  try {
    await fetch(`${enderecoDaApi()}/v1/errors`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        platform: plataformaDoRelato(),
        appVersion: import.meta.env.VITE_APP_VERSION ?? "0.0.0",
        message: mensagem,
        ...(erro.stack ? { stack: erro.stack.slice(0, 20_000) } : {}),
        context: { url: typeof window === "undefined" ? "" : window.location.pathname, ...contexto },
      }),
      keepalive: true,
    });
  } catch {
    // Sem rede não há para quem relatar, e o jogo segue.
  }
}

/** Liga os ganchos de erro global. Chamado uma vez, na abertura. */
export function iniciarRelatoAoServidor(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("error", (evento) => {
    void relatarAoServidor(evento.error ?? evento.message);
  });
  window.addEventListener("unhandledrejection", (evento) => {
    void relatarAoServidor(evento.reason);
  });
}

export { Sentry };
